/**
 * CompanyDashboard - Hub for all company settings
 *
 * Displays cards linking to:
 * - Users (invites, roles)
 * - Roles (custom RBAC)
 * - FTP Sources (photographer credentials)
 * - FTP Targets (output destinations)
 * - Profiles (processing presets)
 * - Settings (general company config)
 */

import { useState, useEffect } from 'react';
import { fetchCompanyStats } from '../../../../api';
import Spinner from '../../../../shared/components/ui/Spinner';
import styles from './CompanyDashboard.module.css';

const SECTIONS = [
  {
    id: 'users',
    title: 'Users',
    description: 'Manage team members, send invites, assign roles',
    icon: 'U',
    hash: 'company/users',
    statKey: 'user_count',
    statLabel: 'users',
  },
  {
    id: 'roles',
    title: 'Roles',
    description: 'Create custom roles with granular permissions',
    icon: 'R',
    hash: 'company/roles',
    statKey: 'role_count',
    statLabel: 'roles',
  },
  {
    id: 'ftp-sources',
    title: 'FTP Sources',
    description: 'Photographer upload credentials and folders',
    icon: 'S',
    hash: 'company/ftp-sources',
    statKey: 'ftp_source_count',
    statLabel: 'sources',
  },
  {
    id: 'ftp-targets',
    title: 'FTP Targets',
    description: 'Output destinations for processed photos',
    icon: 'T',
    hash: 'company/ftp-targets',
    statKey: 'ftp_target_count',
    statLabel: 'targets',
  },
  {
    id: 'profiles',
    title: 'Profiles',
    description: 'Image processing presets and styles',
    icon: 'P',
    hash: 'company/profiles',
    statKey: 'profile_count',
    statLabel: 'profiles',
  },
  {
    id: 'settings',
    title: 'Settings',
    description: 'Company name, defaults, and preferences',
    icon: 'G',
    hash: 'company/settings',
    statKey: null,
    statLabel: null,
  },
  {
    id: 'caption-builder',
    title: 'Caption Builder',
    description: 'Action templates, connectors, grammar rules',
    icon: 'CB',
    hash: 'company/caption-builder',
    statKey: null,
    statLabel: null,
  },
];

// Hash-based navigation helper
const navigateHash = (hash) => {
  window.location.hash = hash;
};

function StatCard({ section, stat, onClick }) {
  return (
    <div className={styles.card} onClick={onClick}>
      <div className={styles.cardIcon}>{section.icon}</div>
      <div className={styles.cardContent}>
        <h3 className={styles.cardTitle}>{section.title}</h3>
        <p className={styles.cardDescription}>{section.description}</p>
        {section.statKey && stat !== undefined && (
          <div className={styles.cardStat}>
            <span className={styles.statValue}>{stat}</span>
            <span className={styles.statLabel}>{section.statLabel}</span>
          </div>
        )}
      </div>
      <div className={styles.cardArrow}>&rarr;</div>
    </div>
  );
}

export default function CompanyDashboard() {
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadStats = async () => {
      try {
        const data = await fetchCompanyStats();
        setStats(data);
      } catch (err) {
        console.error('Failed to load company stats:', err);
        // Set defaults on error
        setStats({
          user_count: 0,
          role_count: 0,
          ftp_source_count: 0,
          ftp_target_count: 0,
          profile_count: 0,
        });
      } finally {
        setLoading(false);
      }
    };
    loadStats();
  }, []);

  if (loading) {
    return (
      <div className={styles.loading}>
        <Spinner />
        <p>Loading company settings...</p>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h2 className={styles.title}>Company Settings</h2>
        <p className={styles.subtitle}>
          Configure users, permissions, FTP connections, and processing profiles
        </p>
      </div>

      <div className={styles.grid}>
        {SECTIONS.map((section) => (
          <StatCard
            key={section.id}
            section={section}
            stat={section.statKey ? stats[section.statKey] : undefined}
            onClick={() => navigateHash(section.hash)}
          />
        ))}
      </div>

      <div className={styles.quickActions}>
        <h3 className={styles.quickActionsTitle}>Quick Actions</h3>
        <div className={styles.quickActionsList}>
          <button
            className={styles.quickAction}
            onClick={() => navigateHash('company/users?action=invite')}
          >
            Invite New User
          </button>
          <button
            className={styles.quickAction}
            onClick={() => navigateHash('company/ftp-sources?action=new')}
          >
            Add FTP Source
          </button>
          <button
            className={styles.quickAction}
            onClick={() => navigateHash('company/ftp-targets?action=new')}
          >
            Add FTP Target
          </button>
        </div>
      </div>
    </div>
  );
}
